import {Alert, Box, Button, CircularProgress, Stack, Typography} from "@mui/material";
import {SurveyQuestion} from "crqlar-react-common/dist/models/survey/surveyQuestion.model";
import {
    SurveyBlackoutStateDTO,
    SurveyRedactionField
} from "crqlar-react-common/dist/models/survey/surveyRedaction.model";
import {SurveyAnswer} from "crqlar-react-common/dist/models/survey/surveySubmission.model";
import {SurveyService} from "crqlar-react-common/dist/services/survey/survey.service";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import {enqueueSnackbar} from "notistack";
import {forwardRef, Fragment, useEffect, useImperativeHandle, useMemo, useRef, useState} from "react";
import {useQuery, useQueryClient} from "react-query";
import {SurveyAnswerContext, SurveyAnswerValue, surveyQuestionTitle} from "../../shared/surveyAnswer.util";
import {useSurveyReviewRefresh} from "../../shared/surveyQueryRefresh";
import {
    SURVEY_ANSWER_BLOCK_GAP,
    SurveyAnswerNotAnswered,
    surveyAnswerValueSx,
    SurveyQuestionTitleRow
} from "../surveySubmissionAnswerLayout";
import SurveyBlackoutBar, {surveyBlackoutLockedSx, surveyBlackoutTextSx} from "./SurveyBlackoutBar";
import SurveyBlackoutConfirmBubble from "./SurveyBlackoutConfirmBubble";
import {
    clearTextSelection,
    SurveyBlackoutContainer,
    SurveyBlackoutOffsets,
    surveyBlackoutSelectionRect
} from "./surveyBlackoutOffsets";
import {
    buildSurveyBlackoutSegments,
    mergeSurveyBlackoutRange,
    SurveyBlackoutRange
} from "./surveyBlackoutSegments";
import {useSurveyBlackoutArming} from "./useSurveyBlackoutArming";
import {useSurveyBlackoutSelection} from "./useSurveyBlackoutSelection";

interface Props {
    submissionId: string
    questions: SurveyQuestion[]
    ctx: SurveyAnswerContext
    /** Reports a save in flight, so the caller can show it beside the switch that opened this. */
    onBusyChange?: (busy: boolean) => void
    /** Reports whether staged changes are waiting for Save, so the caller can offer it. */
    onDirtyChange?: (dirty: boolean) => void
}

export interface SurveyBlackoutPaneHandle {
    /** Sends every staged change. Resolves true once all of them have landed. */
    save: () => Promise<boolean>
    /** Drops every staged change without sending it. */
    discard: () => void
}

/** One redactable container: the guest's own words for a single question and field. */
interface BlackoutField {
    key: string
    questionId: string
    field: SurveyRedactionField
    text: string
}

interface QuestionBlock {
    question: SurveyQuestion
    title: string
    /** The answer minus the guest's typed text: options, scores, dates, media — rendered read-only. */
    restAnswer?: SurveyAnswer
    answered: boolean
    fields: BlackoutField[]
}

/** A blackout drawn in this session and not sent yet: it exists only on screen until Save. */
interface StagedAdd {
    id: string
    key: string
    questionId: string
    field: SurveyRedactionField
    offsets: SurveyBlackoutOffsets
}

/** Tells a staged bar from a stored one, which is how a click knows whether to undo or to stage a removal. */
const STAGED_ID_PREFIX = 'staged-';

/** Answers a blackout can never touch stay for context but fade, so the typed text reads as the only thing to select. */
const BLACKOUT_INERT_OPACITY = 0.45;

/**
 * Blackout mode: the original answers, rendered as plain selectable text.
 *
 * Apply is a two-step — select, then confirm in a bubble anchored to the selection. Restore is the
 * same shape in reverse — one click arms a bar, the next restores it. Nothing reaches the server
 * until the caller invokes `save` on the handle: confirmed bars and restores are staged, shown at
 * once, and sent together, so the dialog can offer an explicit Save.
 *
 * Every answerable question appears here, in the same shape and the same typography the read-only
 * view gives it, because the switch above swaps one for the other in place.
 */
const SurveyBlackoutPane = forwardRef<SurveyBlackoutPaneHandle, Props>((
    {submissionId, questions, ctx, onBusyChange, onDirtyChange},
    ref
) => {
    const {t} = useLocalize();
    const queryClient = useQueryClient();
    const refreshReviewData = useSurveyReviewRefresh();
    const queryKey = ['surveyBlackout', submissionId];
    const {data, isLoading, isError, refetch} = useQuery(
        queryKey,
        () => SurveyService.blackoutOpen({submissionId}),
        {cacheTime: 0, refetchOnWindowFocus: false}
    );

    const [stagedAdds, setStagedAdds] = useState<StagedAdd[]>([]);
    const [stagedRemovals, setStagedRemovals] = useState<string[]>([]);
    const [busy, setBusy] = useState(false);
    const busyRef = useRef(false);
    const nextStagedIdRef = useRef(0);

    const dirty = stagedAdds.length > 0 || stagedRemovals.length > 0;

    useEffect(() => onBusyChange?.(busy), [busy, onBusyChange]);
    useEffect(() => onDirtyChange?.(dirty), [dirty, onDirtyChange]);

    /** Live text containers, in DOM order, so a selection can be resolved back to its field. */
    const containersRef = useRef(new Map<string, HTMLElement>());
    const bubbleRef = useRef<HTMLDivElement | null>(null);

    const blocks = useMemo<QuestionBlock[]>(() => questions.map(question => {
        const questionId = question.id;
        const answer = data?.answers.find(candidate => candidate.questionId === questionId);
        const fields: BlackoutField[] = [];

        const addField = (field: SurveyRedactionField, text: string | undefined) => {
            // The text is taken verbatim — no trim, no normalisation. Stored offsets address this
            // exact string, so anything that edits it on the way to the screen breaks them.
            //
            // WHY no question-type check: the answer's own data decides. A field is offered
            // whenever the guest actually typed into it, rather than when a whitelist of question
            // types — a second copy of the backend's, free to drift from it — says it may be.
            if (!questionId || !text?.length) return;
            fields.push({key: `${questionId}:${field}`, questionId, field, text});
        };

        // The order the read-only renderer lays these out in, so the switch does not reshuffle them.
        addField(SurveyRedactionField.CUSTOM_TEXT, answer?.customText);
        addField(SurveyRedactionField.TEXT_VALUE, answer?.textValue);

        const editable = new Set(fields.map(field => field.field));

        return {
            question,
            title: surveyQuestionTitle(question, ctx),
            // Only the fields actually pulled out above are taken off this remnant, so no value can
            // fall into the gap between "rendered as selectable text" and "rendered read-only".
            restAnswer: answer && {
                ...answer,
                customText: editable.has(SurveyRedactionField.CUSTOM_TEXT) ? undefined : answer.customText,
                textValue: editable.has(SurveyRedactionField.TEXT_VALUE) ? undefined : answer.textValue,
            },
            answered: !!answer,
            fields,
        };
    }), [questions, data, ctx]);

    const fieldsByKey = useMemo(
        () => new Map(blocks.flatMap(block => block.fields.map(field => [field.key, field] as const))),
        [blocks]
    );

    const {selection, clearSelection} = useSurveyBlackoutSelection({
        getContainers: () => {
            const containers: SurveyBlackoutContainer[] = [];
            containersRef.current.forEach((element, key) => containers.push({key, element}));
            return containers;
        },
        ignoreRef: bubbleRef,
    });

    // A staged bar is simply taken back; a stored one is queued for removal on Save.
    const handleRestore = (barId: string) => {
        if (barId.startsWith(STAGED_ID_PREFIX)) {
            setStagedAdds(previous => previous.filter(add => add.id !== barId));
            return;
        }

        setStagedRemovals(previous => previous.includes(barId) ? previous : [...previous, barId]);
    };

    const {armedId, handleBarClick, disarm} = useSurveyBlackoutArming({onRestore: handleRestore, disabled: busy});

    const handleApply = () => {
        if (!selection) return;

        const target = fieldsByKey.get(selection.key);
        if (!target) return;

        // The offsets come from the settled selection rather than from the live one — by click time
        // the browser may well have collapsed it.
        const offsets = selection.offsets;
        clearTextSelection();
        clearSelection();
        disarm();

        nextStagedIdRef.current += 1;
        setStagedAdds(previous => [...previous, {
            id: `${STAGED_ID_PREFIX}${nextStagedIdRef.current}`,
            key: target.key,
            questionId: target.questionId,
            field: target.field,
            offsets,
        }]);
    };

    const save = async (): Promise<boolean> => {
        if (busyRef.current) return false;
        if (!dirty) return true;

        busyRef.current = true;
        setBusy(true);
        disarm();

        // Removals go first: an add that touches a range still on the server is merged into it,
        // and the id of a range merged away like that can no longer be removed afterwards.
        const removals = [...stagedRemovals];
        const adds = [...stagedAdds];
        let latest: SurveyBlackoutStateDTO | undefined;
        let failed = false;

        try {
            while (removals.length) {
                latest = await SurveyService.blackoutRemove({submissionId, redactionId: removals[0]});
                removals.shift();
            }
            while (adds.length) {
                const add = adds[0];
                latest = await SurveyService.blackoutAdd({
                    submissionId,
                    questionId: add.questionId,
                    field: add.field,
                    start: add.offsets.start,
                    end: add.offsets.end,
                });
                adds.shift();
            }
        } catch {
            failed = true;
            enqueueSnackbar(t('Could not complete this action. Please try again.'), {variant: 'error'});
        }

        // Whatever landed is shown as the server has it; whatever did not stays staged for another try.
        if (latest) queryClient.setQueryData(queryKey, latest);
        setStagedRemovals(removals);
        setStagedAdds(adds);
        if (latest) await refreshReviewData();

        busyRef.current = false;
        setBusy(false);
        return !failed;
    };

    const discard = () => {
        setStagedAdds([]);
        setStagedRemovals([]);
        clearTextSelection();
        clearSelection();
        disarm();
    };

    useImperativeHandle(ref, () => ({save, discard}));

    /** Stored ranges for one field, with the staged changes folded in. */
    const rangesOf = (field: BlackoutField): SurveyBlackoutRange[] => {
        const stored = (data?.redactions ?? [])
            .filter(entry => (
                entry.questionId === field.questionId && entry.field === field.field
                && entry.id && entry.start !== undefined && entry.end !== undefined
                && !stagedRemovals.includes(entry.id)
            ))
            .map(entry => ({id: entry.id!, start: entry.start!, end: entry.end!}));

        // Merged rather than appended, because that is what the server will do with it on Save.
        return stagedAdds
            .filter(add => add.key === field.key)
            .reduce((ranges, add) => mergeSurveyBlackoutRange(ranges, add.offsets, add.id), stored);
    };

    return (
        <Stack gap={3}>
            {isLoading && <CircularProgress size={24}/>}
            {isError && (
                <Alert severity={'error'} action={<Button onClick={() => void refetch()}>{t('Retry')}</Button>}>
                    {t('Could not complete this action. Please try again.')}
                </Alert>
            )}

            {data && blocks.map(block => (
                <Stack
                    key={block.question.id}
                    gap={SURVEY_ANSWER_BLOCK_GAP}
                    sx={{minWidth: 0, opacity: block.fields.length ? 1 : BLACKOUT_INERT_OPACITY}}
                >
                    <SurveyQuestionTitleRow question={block.question} ctx={ctx}/>

                    <Typography variant={'body1'} component={'div'} sx={surveyAnswerValueSx}>
                        {/*
                            Everything the rule leaves out, grouped and visibly inert. An answer
                            without any typed text has no fields at all, so its placeholder belongs
                            in here too. A block that has typed text fades only this part; one
                            without fades as a whole above, so nothing is dimmed twice.
                        */}
                        <Box sx={{...surveyBlackoutLockedSx, opacity: block.fields.length ? BLACKOUT_INERT_OPACITY : 1}}>
                            <SurveyAnswerValue answer={block.restAnswer} question={block.question} ctx={ctx}/>
                            {!block.answered && <SurveyAnswerNotAnswered label={t('Not answered')}/>}
                        </Box>

                        {block.fields.map(field => (
                            <Box
                                key={field.key}
                                ref={(node: HTMLDivElement | null) => {
                                    if (node) containersRef.current.set(field.key, node);
                                    else containersRef.current.delete(field.key);
                                }}
                                aria-label={block.title}
                                sx={surveyBlackoutTextSx}
                            >
                                {buildSurveyBlackoutSegments(field.text, rangesOf(field)).map(segment => {
                                    const range = segment.range;
                                    if (!range) return <Fragment key={segment.key}>{segment.text}</Fragment>;

                                    const staged = range.id.startsWith(STAGED_ID_PREFIX);
                                    const armed = armedId === range.id;
                                    const tooltip = staged
                                        ? (armed ? t('Unsaved · Click again to undo') : t('Unsaved · Click to undo'))
                                        : (armed ? t('Click again to restore') : t('Click to restore'));

                                    return (
                                        <SurveyBlackoutBar
                                            key={segment.key}
                                            barId={range.id}
                                            text={segment.text}
                                            armed={armed}
                                            pending={staged}
                                            tooltip={tooltip}
                                            onClick={() => handleBarClick(range.id)}
                                        />
                                    );
                                })}
                            </Box>
                        ))}
                    </Typography>
                </Stack>
            ))}

            {selection && (
                <SurveyBlackoutConfirmBubble
                    // Live rect first, so the bubble tracks the text while the pane scrolls; the
                    // rect captured when the selection settled is the standing fallback.
                    getAnchorRect={() =>
                        surveyBlackoutSelectionRect(containersRef.current.get(selection.key)) || selection.anchor}
                    contextElement={containersRef.current.get(selection.key)}
                    bubbleRef={bubbleRef}
                    label={t('Black out')}
                    onApply={handleApply}
                    disabled={busy}
                />
            )}
        </Stack>
    );
});

SurveyBlackoutPane.displayName = 'SurveyBlackoutPane';

export default SurveyBlackoutPane;
